import { AuthenticationResult } from "@azure/msal-browser";
import { SchedulePatch } from "../utils/schedulePatch";

export type { SchedulePatch, SchedulePatchChange } from "../utils/schedulePatch";

// Schedule API interfaces
export interface ScheduleRequest {
  year: number;
  week: number;
  number: number;
}

export interface ScheduleComment {
  [weekKey: string]: string;
}

export interface ScheduleHours {
  [weekKey: string]: number;
}

export interface ScheduleActivity {
  activity: string;
  activity_id: number;
  client: string;
  comments: ScheduleComment;
  hour_type: string;
  hours: ScheduleHours;
  name: string;
  parent_id: number;
  person: number;
  project: string;
  reference: string;
  reservation: boolean;
}

export interface SchedulePerson {
  children: ScheduleActivity[];
  hours: ScheduleHours;
  id: number;
  mode: string;
  name: string;
  reservation: Record<string, boolean>;
  schedule_remarks?: ScheduleRemark[];
}

export type ScheduleRemarkType = "off" | "constraint" | "note";

export interface ScheduleRemark {
  id: number;
  remark_type: ScheduleRemarkType;
  days?: string | null;
  text: string;
  start_week: number;
  end_week?: number | null;
}

export interface ScheduleRemarkInput {
  remark_type: ScheduleRemarkType;
  days?: string | null;
  text: string;
  start_week: number;
  end_week?: number | null;
  close_previous?: boolean;
}

export type ScheduleResponse = SchedulePerson[];

export interface ScheduleChangesResponse {
  revision: number;
  snapshot_required: boolean;
  patches: SchedulePatch[];
}

// Project Management API interfaces
export interface Client {
  id: number;
  name: string;
  external: boolean;
}

export interface Person {
  id: number;
  name: string;
  surname: string;
  email: string;
  plannable: boolean;
}

export interface ProjectStatus {
  id: number;
  name: string;
  completed: boolean;
  win: boolean;
}

export interface Activity {
  id: number;
  name: string;
  hour_type: 'billable' | 'non_billable' | 'non_working';
  project_id: number;
  service_type_id?: number;
  schedulable: boolean;
}

export interface ActivityCreate {
  id?: number;
  name: string;
  hour_type: 'billable' | 'non_billable' | 'non_working';
  project_id: number;
  service_type_id?: number;
  schedulable: boolean;
}

export interface ServiceType {
  id: number;
  name: string;
}

/** App RBAC role from /roles and /me/roles */
export interface AppRole {
  id: number;
  role_key: string;
  name: string;
  description: string;
}

/** Role assignment on a person (includes assignment metadata) */
export interface PersonRoleAssignment extends AppRole {
  assigned_at: string;
  assigned_by: string;
}

/** Person listed under a role via /roles/:id/people */
export interface RolePerson {
  id: number;
  name: string;
  surname: string;
  email: string;
  assigned_at: string;
  assigned_by: string;
}

export interface Project {
  id: number;
  reference?: string;
  name: string;
  hour_type: 'billable' | 'non_billable' | 'non_working';
  fee_total?: number;
  fee_hourly?: number;
  client: Client;
  manager?: Person;
  status: ProjectStatus;
  reservation: boolean;
  activities?: Activity[];
}

export interface ProjectCreate {
  id?: number;
  reference?: string;
  name: string;
  hour_type: 'billable' | 'non_billable' | 'non_working';
  fee_total?: number;
  fee_hourly?: number;
  client: Client;
  manager?: number; // ID reference for creation
  status: ProjectStatus;
  reservation: boolean;
}

export interface RedTeamDomain {
  id: number;
  domainname: string;
  compromised: boolean;
  relay: boolean;
  website: boolean;
  phishing: boolean;
  tenant: string;
  reserved: boolean;
  project_reference?: string;
  project_name?: string;
  reservation_comment?: string;
}

export interface RedTeamTenant {
  id: number;
  tenant_id: string;
  tenant_name: string;
  tenant_tf_name: string;
  phishing_tenant: boolean;
  relay_tenant: boolean;
  is_reserved: boolean;
  tenant_provider?: string;
  project_reference?: string;
  comment?: string;
}

export interface RedTeamTenantReservation {
  project_reference: string;
  project_name: string;
  reserved_at?: string;
  comment?: string;
}

export interface RedTeamTenantListItem extends RedTeamTenant {
  in_use: boolean;
  reservations: RedTeamTenantReservation[];
}

export type AzureRelayRegion =
  | 'europe'
  | 'america_south'
  | 'america_north'
  | 'asia'
  | 'middle_east'
  | 'africa';
export type BackendType = 'morpheus' | 'mythic' | 'cobaltstrike' | 'teams' | 'c3';

export interface InfraBackendInput {
  type: BackendType;
  /** Applied to attached relays linked to this backend. */
  relay_profile_type?: string;
}

export type CDNProvider = "azure" | "frontdoor" | "fastly";

export interface InfraCDNSlot {
  provider: CDNProvider;
  /** Omit or leave empty for a random name. */
  domain_fixed?: string;
}

export interface InfraRelayInput {
  type: string;
  connected_to_backend: number;
  relay_profile_type?: string;
  domain?: string;
  cdn_slots?: InfraCDNSlot[];
  /** @deprecated Prefer cdn_slots; kept for reading legacy drafts. */
  cdn_domains_count?: number;
  azure_relay_location?: string;
  standalone?: boolean;
}

export interface InfraWireGuardInput {
  azure_relay_location?: string;
}

export interface InfrastructureCreatePayload {
  project_reference: string;
  project_code?: string;
  project_codename: string;
  owner: string;
  created_by: string;
  azure_rg_location?: string;
  azure_relay_region: AzureRelayRegion;
  project_team: string[];
	tenant_separation?: boolean;
  tenant_id?: number;
  webhook_url?: string;
  channel_name?: string;
  client_key?: string;
  backends: InfraBackendInput[];
  relays: InfraRelayInput[];
  wireguards: InfraWireGuardInput[];
  based_on_id?: number;
}

export interface InfrastructureRequest {
  id: number;
  project_id: number;
  project_reference: string;
  tenant_id: number;
  tenant_provider?: string;
  client_key: string;
  status: string;
  request_json: unknown;
  terraform_json: unknown;
  created_by_email: string;
  created_at?: string;
  updated_at?: string;
  terraform_task_completed_at?: string | null;
  phishing_task_completed_at?: string | null;
  backup_task_completed_at?: string | null;
  phishing_task_needed?: boolean;
  backup_task_needed?: boolean;
  terraform_task_done?: boolean;
  phishing_task_done?: boolean;
  backup_task_done?: boolean;
  tasks_incomplete?: boolean;
}

export interface ProjectTenant {
  id: number;
  tenant_id: string;
  tenant_name: string;
  tenant_tf_name: string;
  phishing_tenant: boolean;
  relay_tenant: boolean;
  tenant_provider?: string;
  comment?: string;
}

export interface PhishingDomainCandidate {
  id: number;
  domainname: string;
  source: 'available' | 'project';
  phishing?: boolean;
  relay?: boolean;
  relay_tenant_id?: number | null;
  phishing_tenant_id?: number | null;
  phishing_status?: string;
}

export interface PhishingDomainCurrent {
  domain_id: number;
  domainname: string;
  phishing_tenant_id?: number | null;
  relay_tenant_id?: number | null;
  phishing_status: string;
  subdomains: string[];
  comment?: string;
  jobs?: PhishingJob[];
}

export interface PhishingJob {
  fqdn: string;
  action: string;
  instance_id: string;
  status_query_uri?: string;
  send_event_post_uri?: string;
  purge_history_delete_uri?: string;
  terminate_post_uri?: string;
  resume_post_uri?: string;
  rewind_post_uri?: string;
  restart_post_uri?: string;
  suspend_post_uri?: string;
  runtime_status: string;
  created_time?: string;
  last_updated_time?: string;
  updated_at?: string;
  error?: string;
}

export interface PhishingOngoingJob {
  domain_id: number;
  domainname: string;
  fqdn: string;
  action: string;
  instance_id: string;
  runtime_status: string;
  updated_at?: string;
  last_updated_time?: string;
  error?: string;
  can_terminate?: boolean;
  can_restart?: boolean;
}

export interface PhishingDomainsResponse {
  candidates: PhishingDomainCandidate[];
  current: PhishingDomainCurrent[];
  ongoing?: PhishingOngoingJob[];
}

export interface PhishingJobsRefreshResponse {
  message: string;
  ongoing: PhishingOngoingJob[];
  updated: unknown[];
  credentials: Array<{
    domainname: string;
    fqdn: string;
    user_upn: string;
    user_password: string;
  }>;
}

// Timesheet API interfaces
export interface TimesheetWeek {
  complete: boolean;
  week: number; // WW format as number
  week_id: number; // YYYYWW format as number
  year: number; // YYYY format as number
}

export interface TimesheetWeeksResponse {
  todo: TimesheetWeek[];
  completed: TimesheetWeek[];
}

export interface TimesheetEntry {
  activity: string;
  activity_id: number;
  actuals: number;
  client: string;
  comment: string;
  hour_type: string;
  planned: number | null;
  project: string;
  reference: string;
  billable: boolean;
}

// Hour analysis interfaces
export interface HourAnalysisEntry {
  activity: string;
  activity_id: number;
  actuals: number;
  client: string;
  comment: string;
  hour_type: string;
  planned: number | null;
  project: string;
  reference: string;
  person_id: number;
}

export interface HourAnalysisGroup {
  availability: number;
  name: string;
  person_id: number;
  timesheet_complete: boolean;
}

export interface HourAnalysisResponse {
  data: HourAnalysisEntry[];
  groups: HourAnalysisGroup[];
}

export interface TimesheetWeekResponse {
  entries: TimesheetEntry[];
}

export interface TimesheetSaveEntry {
  actuals: number;
  activity_id: number;
  comment: string;
  billable: boolean;
}

export interface TimesheetSaveRequest {
  week: number; // YYYYWW format as number
  complete: boolean;
  data: TimesheetSaveEntry[];
}

// Activities dropdown interfaces
export interface ActivityProject {
  client: {
    name: string;
  };
  name: string;
  reference: string;
}

export interface ActivityDropdownItem {
  hour_type: string;
  id: number;
  name: string;
  project?: ActivityProject; // Make project optional to handle null/undefined cases
}

export type ActivityDropdownResponse = ActivityDropdownItem[];

// People dropdown interfaces
export interface PeopleDropdownItem {
  id: number;
  name: string;
  surname: string;
}

export type PeopleDropdownResponse = PeopleDropdownItem[];

// Availability interface
export interface Availability {
  startweek: number;
  hours: number;
}

export interface ApiResponse<T = any> {
  data: T;
  success: boolean;
  message?: string;
  revision?: number;
}

type TokenRefreshCallback = (forceRefresh?: boolean) => Promise<boolean>;

export class ApiService {
  private baseUrl: string;
  private idToken: string | null = null;
  private tokenRefreshCallback: TokenRefreshCallback | null = null;
  private authFailureCallback: (() => void) | null = null;

  constructor() {
    this.baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8081/api/v1';
  }

  /**
   * Sets the ID token for API requests
   */
  setIdToken(authResult: AuthenticationResult | null): void {
    this.idToken = authResult?.idToken || null;
  }

  /**
   * Sets a callback function to refresh the token when needed
   */
  setTokenRefreshCallback(callback: TokenRefreshCallback): void {
    this.tokenRefreshCallback = callback;
  }

  /**
   * Called when a request is still unauthorized after a forced token refresh.
   * Marks the session as expired so the UI can prompt for interactive login.
   */
  setAuthFailureCallback(callback: () => void): void {
    this.authFailureCallback = callback;
  }

  /**
   * Ensures we have a fresh token before making requests
   */
  private async ensureFreshToken(): Promise<boolean> {
    // If token is not expiring soon, we can use the existing one
    if (!this.isTokenExpiringSoon(60)) { // Refresh 60 seconds before expiration
      return this.idToken !== null;
    }

    // Token is expiring soon or doesn't exist, try to refresh.
    // Force Azure to issue a new token when the current JWT is already expired;
    // MSAL can otherwise return a cached ID token that the API will still reject.
    if (this.tokenRefreshCallback) {
      try {
        const forceRefresh = this.isTokenExpiringSoon(0);
        const refreshed = await this.tokenRefreshCallback(forceRefresh);
        if (refreshed) {
          return true;
        }
      } catch (error) {
        console.warn('Token refresh failed:', error);
      }
    }

    // Refresh was skipped (MSAL busy) or failed. Keep using the current token
    // if it is still within its actual expiry so a 60s buffer does not drop requests.
    return this.idToken !== null && !this.isTokenExpiringSoon(0);
  }

  /**
   * Checks if the current token expires within the specified seconds
   */
  private isTokenExpiringSoon(bufferSeconds: number = 30): boolean {
    if (!this.idToken) {
      return true; // No token means it's "expired"
    }

    try {
      // Decode JWT token (just the payload, no verification needed for expiry check)
      const base64Url = this.idToken.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      
      const payload = JSON.parse(jsonPayload);
      const exp = payload.exp; // Expiration time in seconds since Unix epoch
      
      if (!exp) {
        return true; // No expiration claim means we should refresh
      }
      
      const currentTime = Math.floor(Date.now() / 1000); // Current time in seconds
      const expirationTime = exp - bufferSeconds; // Buffer time before actual expiration
      
      return currentTime >= expirationTime;
    } catch (error) {
      console.error('Error checking token expiration:', error);
      return true; // If we can't decode, assume it's expired
    }
  }

  /**
   * Gets common headers for API requests
   */
  private getHeaders(): HeadersInit {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };
    
    if (this.idToken) {
      headers['Authorization'] = `Bearer ${this.idToken}`;
    }

    return headers;
  }

  /**
   * Extracts a human-readable error message from a failed API response.
   */
  private async parseErrorMessage(response: Response): Promise<string> {
    try {
      const contentType = response.headers.get('content-type');
      if (contentType?.includes('application/json')) {
        const body = await response.json();
        if (typeof body?.message === 'string' && body.message.trim()) {
          return body.message;
        }
        if (body && typeof body === 'object') {
          const fieldMessages = Object.values(body).find(
            (value): value is string[] =>
              Array.isArray(value) && typeof value[0] === 'string',
          );
          if (fieldMessages?.[0]) {
            return fieldMessages[0];
          }
        }
      } else {
        const text = await response.text();
        if (text.trim()) {
          return text;
        }
      }
    } catch {
      // Fall through to generic message below.
    }
    return `HTTP error! status: ${response.status}`;
  }

  /**
   * Makes a request with automatic token refresh and retry logic
   */
  private async makeRequest<T>(
    url: string, 
    options: RequestInit
  ): Promise<ApiResponse<T>> {
    // Ensure we have a fresh token before making the request
    const tokenReady = await this.ensureFreshToken();
    if (!tokenReady) {
      return {
        data: null as T,
        success: false,
        message: 'Failed to acquire authentication token',
      };
    }

    // Update headers with fresh token
    const requestOptions = {
      ...options,
      headers: {
        ...options.headers,
        ...this.getHeaders()
      }
    };

    try {
      let response = await fetch(url, requestOptions);

      // If we get a 401, force-refresh (bypass MSAL cache) and retry once.
      // A silent cache hit can return the same ID token the API just rejected.
      if (response.status === 401 && this.tokenRefreshCallback) {
        const refreshSuccess = await this.tokenRefreshCallback(true);

        if (refreshSuccess) {
          const retryOptions = {
            ...requestOptions,
            headers: {
              ...requestOptions.headers,
              ...this.getHeaders()
            }
          };
          response = await fetch(url, retryOptions);

          if (response.status === 401) {
            this.idToken = null;
            this.authFailureCallback?.();
          }
        }
      }

      if (!response.ok) {
        const message = await this.parseErrorMessage(response);
        throw new Error(message);
      }

      const data = await response.json();
      const revisionHeader = response.headers.get("X-Schedule-Revision");
      const revision = revisionHeader ? Number(revisionHeader) : undefined;
      return {
        data,
        success: true,
        revision: Number.isFinite(revision) ? revision : undefined,
      };
    } catch (error) {
      console.error('API Request Error:', error);
      return {
        data: null as T,
        success: false,
        message: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  /**
   * Makes a GET request to the API
   */
  async get<T>(
    endpoint: string,
    extraHeaders?: Record<string, string>,
  ): Promise<ApiResponse<T>> {
    return this.makeRequest<T>(`${this.baseUrl}${endpoint}`, {
      method: 'GET',
      headers: {
        ...this.getHeaders(),
        ...extraHeaders,
      },
    });
  }

  /**
   * Makes a POST request to the API
   */
  async post<T>(
    endpoint: string,
    body: any,
    extraHeaders?: Record<string, string>,
  ): Promise<ApiResponse<T>> {
    return this.makeRequest<T>(`${this.baseUrl}${endpoint}`, {
      method: 'POST',
      headers: {
        ...this.getHeaders(),
        ...extraHeaders,
      },
      body: JSON.stringify(body),
    });
  }

  /**
   * Makes a PUT request to the API
   */
  async put<T>(
    endpoint: string,
    body: any,
    extraHeaders?: Record<string, string>,
  ): Promise<ApiResponse<T>> {
    return this.makeRequest<T>(`${this.baseUrl}${endpoint}`, {
      method: 'PUT',
      headers: {
        ...this.getHeaders(),
        ...extraHeaders,
      },
      body: JSON.stringify(body),
    });
  }

  /**
   * Makes a DELETE request to the API
   */
  async delete<T>(
    endpoint: string,
    extraHeaders?: Record<string, string>,
  ): Promise<ApiResponse<T>> {
    return this.makeRequest<T>(`${this.baseUrl}${endpoint}`, {
      method: 'DELETE',
      headers: {
        ...this.getHeaders(),
        ...extraHeaders,
      },
    });
  }

  /**
   * Makes a PATCH request to the API
   */
  async patch<T>(
    endpoint: string,
    body: any,
    extraHeaders?: Record<string, string>,
  ): Promise<ApiResponse<T>> {
    return this.makeRequest<T>(`${this.baseUrl}${endpoint}`, {
      method: 'PATCH',
      headers: {
        ...this.getHeaders(),
        ...extraHeaders,
      },
      body: JSON.stringify(body),
    });
  }

  /**
   * Gets schedule data for specified weeks
   */
  async getSchedule(request: ScheduleRequest): Promise<ApiResponse<ScheduleResponse>> {
    return this.post<ScheduleResponse>('/schedule', request);
  }

  /**
   * Fetches schedule patches after a given revision for the visible week window.
   */
  async getScheduleChanges(request: ScheduleRequest & { since: number }): Promise<ApiResponse<ScheduleChangesResponse>> {
    const params = new URLSearchParams({
      since: String(request.since),
      year: String(request.year),
      week: String(request.week),
      number: String(request.number),
    });
    return this.get<ScheduleChangesResponse>(`/schedule/changes?${params.toString()}`);
  }

  /**
   * Gets activities dropdown data for project and activity selection
   */
  async getActivitiesDropdown(): Promise<ApiResponse<ActivityDropdownResponse>> {
    return this.get<ActivityDropdownResponse>('/activities/dropdown');
  }

  /**
   * Gets people dropdown data for employee selection
   */
  async getPeopleDropdown(): Promise<ApiResponse<PeopleDropdownResponse>> {
    return this.get<PeopleDropdownResponse>('/people/dropdown');
  }

  // People API
  async getPeople(): Promise<ApiResponse<Person[]>> {
    return this.get<Person[]>('/people');
  }

  async createOrUpdatePerson(person: { id?: number; name: string; surname: string; email: string; plannable: boolean }): Promise<ApiResponse<Person>> {
    return this.post<Person>(`/people`, person);
  }

  async deletePerson(id: number): Promise<ApiResponse<{}>> {
    return this.delete<{}>(`/people/${id}`);
  }

  async setAvailability(id: number, availability: Availability): Promise<ApiResponse<{}>> {
    return this.post<{}>(`/availability/${id}`, availability);
  }

  async getPersonScheduleRemarks(personId: number): Promise<ApiResponse<ScheduleRemark[]>> {
    return this.get<ScheduleRemark[]>(`/people/${personId}/schedule-remarks`);
  }

  async createPersonScheduleRemark(
    personId: number,
    remark: ScheduleRemarkInput
  ): Promise<ApiResponse<ScheduleRemark>> {
    return this.post<ScheduleRemark>(`/people/${personId}/schedule-remarks`, remark);
  }

  async updatePersonScheduleRemark(
    personId: number,
    remarkId: number,
    remark: ScheduleRemarkInput
  ): Promise<ApiResponse<ScheduleRemark>> {
    return this.put<ScheduleRemark>(
      `/people/${personId}/schedule-remarks/${remarkId}`,
      remark
    );
  }

  async deletePersonScheduleRemark(
    personId: number,
    remarkId: number
  ): Promise<ApiResponse<{ id: number }>> {
    return this.delete<{ id: number }>(
      `/people/${personId}/schedule-remarks/${remarkId}`
    );
  }

  private mutationHeaders(
    clientMutationId?: string,
  ): Record<string, string> | undefined {
    return clientMutationId
      ? { 'X-Client-Mutation-Id': clientMutationId }
      : undefined;
  }

  /**
   * Creates a WebSocket ticket for realtime schedule updates
   */
  async createRealtimeTicket(): Promise<
    ApiResponse<{ ticket: string; expires_at: string; ws_url: string }>
  > {
    return this.post<{ ticket: string; expires_at: string; ws_url: string }>(
      '/realtime/ticket',
      {},
    );
  }

  /**
   * Creates a new event/schedule entry
   */
  async createEvent(
    data: {
      people: number[];
      activity: number;
      hours: number;
      week: number;
      comment?: string;
      recurrence?: {
        endweek: number;
        weeks: number;
      };
    },
    clientMutationId?: string,
  ): Promise<ApiResponse<SchedulePatch>> {
    return this.post<SchedulePatch>(
      '/events',
      data,
      this.mutationHeaders(clientMutationId),
    );
  }

  /**
   * Updates an existing event/schedule entry
   */
  async updateEvent(
    data: {
      person: number;
      hours: number;
      activity: number;
      week: number;
      original_week: number;
      comment?: string;
    },
    clientMutationId?: string,
  ): Promise<ApiResponse<SchedulePatch>> {
    return this.put<SchedulePatch>(
      '/events',
      data,
      this.mutationHeaders(clientMutationId),
    );
  }

  /**
   * Deletes an event/schedule entry
   */
  async deleteEvent(
    data: {
      week: number;
      activity: number;
      person: number;
    },
    clientMutationId?: string,
  ): Promise<ApiResponse<SchedulePatch>> {
    return this.patch<SchedulePatch>(
      '/events/delete',
      data,
      this.mutationHeaders(clientMutationId),
    );
  }

  /**
   * Deletes a range of events/schedule entries
   */
  async deleteEventRange(
    data: {
      people: number[];
      activity: number;
      week_from: number;
      week_to: number;
    },
    clientMutationId?: string,
  ): Promise<ApiResponse<SchedulePatch>> {
    return this.put<SchedulePatch>(
      '/events/range',
      data,
      this.mutationHeaders(clientMutationId),
    );
  }

  // Timesheet API methods

  /**
   * Gets timesheet weeks (both TODO and completed)
   */
  async getTimesheetWeeks(complete: boolean): Promise<ApiResponse<TimesheetWeek[]>> {
    return this.post<TimesheetWeek[]>('/time/weeks', { complete });
  }

  /**
   * Gets timesheet details for a specific week
   */
  async getTimesheetWeek(week: number): Promise<ApiResponse<TimesheetEntry[]>> {
    return this.post<TimesheetEntry[]>('/time/week', { week });
  }

  /**
   * Saves timesheet data for a week
   */
  async saveTimesheet(data: TimesheetSaveRequest): Promise<ApiResponse<any>> {
    return this.put<any>('/time', data);
  }

  /**
   * Gets notification count (e.g., overdue timesheets)
   */
  async getNotifications(): Promise<ApiResponse<{ notifications: number }>> {
    return this.get<{ notifications: number }>('/notifications');
  }

  /**
   * Gets hour analysis data for a specific week
   */
  async getHourAnalysis(week: number): Promise<ApiResponse<HourAnalysisResponse>> {
    return this.post<HourAnalysisResponse>('/houranalysis/week', { week });
  }

  /**
   * Reopens a completed timesheet for a specific week and person
   */
  async reopenTimesheet(week: number, person: number): Promise<ApiResponse<any>> {
    return this.post<any>('/time/reopen', { week, person });
  }

  // Clients API
  async getClients(): Promise<ApiResponse<Array<{ id: number; name: string; external: boolean }>>> {
    return this.get<Array<{ id: number; name: string; external: boolean }>>('/clients');
  }

  async createOrUpdateClient(client: { id?: number; name: string; external: boolean }): Promise<ApiResponse<{ id: number; name: string; external: boolean }>> {
    return this.post<{ id: number; name: string; external: boolean }>('/clients', client);
  }

  async deleteClient(id: number): Promise<ApiResponse<{}>> {
    return this.delete<{}>(`/clients/${id}`);
  }

  // Project Status API
  async getProjectStatuses(): Promise<ApiResponse<ProjectStatus[]>> {
    return this.get<ProjectStatus[]>('/projectstatus');
  }

  async createOrUpdateProjectStatus(projectStatus: { id?: number; name: string; completed: boolean; win: boolean }): Promise<ApiResponse<ProjectStatus>> {
    return this.post<ProjectStatus>(`/projectstatus`, projectStatus);
  }

  async deleteProjectStatus(id: number): Promise<ApiResponse<{}>> {
    return this.delete<{}>(`/projectstatus/${id}`);
  }

  // Projects API
  async getProjects(): Promise<ApiResponse<Project[]>> {
    return this.get<Project[]>('/projects');
  }

  async createOrUpdateProject(project: ProjectCreate): Promise<ApiResponse<Project>> {
    if (project.id) {
      return this.put<Project>(`/projects/${project.id}`, project);
    } else {
      return this.post<Project>('/projects', project);
    }
  }

  async deleteProject(id: number): Promise<ApiResponse<{}>> {
    return this.delete<{}>(`/projects/${id}`);
  }

  // Activities API
  async createOrUpdateActivity(activity: ActivityCreate): Promise<ApiResponse<Activity>> {
    return this.post<Activity>('/activities', activity);
  }

  async deleteActivity(id: number): Promise<ApiResponse<{}>> {
    return this.delete<{}>(`/activities/${id}`);
  }

  // Service Types API
  async getServiceTypes(): Promise<ApiResponse<ServiceType[]>> {
    return this.get<ServiceType[]>('/servicetypes');
  }

  async createOrUpdateServiceType(serviceType: { id?: number; name: string }): Promise<ApiResponse<ServiceType>> {
    return this.post<ServiceType>(`/servicetypes`, serviceType);
  }

  async deleteServiceType(id: number): Promise<ApiResponse<{}>> {
    return this.delete<{}>(`/servicetypes/${id}`);
  }

  // App RBAC roles
  async getMyRoles(): Promise<ApiResponse<AppRole[]>> {
    return this.get<AppRole[]>('/me/roles');
  }

  async getRoles(): Promise<ApiResponse<AppRole[]>> {
    return this.get<AppRole[]>('/roles');
  }

  async createRole(role: {
    role_key: string;
    name: string;
    description?: string;
  }): Promise<ApiResponse<AppRole>> {
    return this.post<AppRole>('/roles', role);
  }

  async updateRole(
    id: number,
    role: { role_key?: string; name?: string; description?: string },
  ): Promise<ApiResponse<AppRole>> {
    return this.put<AppRole>(`/roles/${id}`, role);
  }

  async deleteRole(id: number): Promise<ApiResponse<{ message?: string }>> {
    return this.delete<{ message?: string }>(`/roles/${id}`);
  }

  async getRolePeople(roleId: number): Promise<ApiResponse<RolePerson[]>> {
    return this.get<RolePerson[]>(`/roles/${roleId}/people`);
  }

  async getPersonRoles(personId: number): Promise<ApiResponse<PersonRoleAssignment[]>> {
    return this.get<PersonRoleAssignment[]>(`/people/${personId}/roles`);
  }

  async assignPersonRole(
    personId: number,
    payload: { role_id?: number; role_key?: string },
  ): Promise<ApiResponse<PersonRoleAssignment>> {
    return this.post<PersonRoleAssignment>(`/people/${personId}/roles`, payload);
  }

  async unassignPersonRole(
    personId: number,
    roleId: number,
  ): Promise<ApiResponse<{ message?: string }>> {
    return this.delete<{ message?: string }>(`/people/${personId}/roles/${roleId}`);
  }

  // Weeks management
  async generateWeeks(): Promise<ApiResponse<{}>> {
    return this.get<{}>('/weeks');
  }

  // Search API
  async search(text: string): Promise<ApiResponse<{ projects: Array<{ id: number; name: string; reference: string; client: { name: string } }> }>> {
    return this.post<{ projects: Array<{ id: number; name: string; reference: string; client: { name: string } }> }>('/search', { text });
  }

  // Red Team API
  async getRedTeamDomains(params?: { status?: string; q?: string }): Promise<ApiResponse<RedTeamDomain[]>> {
    const query = new URLSearchParams();
    if (params?.status) query.set('status', params.status);
    if (params?.q) query.set('q', params.q);
    const qs = query.toString();
    return this.get<RedTeamDomain[]>(`/red-team/domains${qs ? `?${qs}` : ''}`);
  }

  async getAvailableRedTeamTenants(): Promise<ApiResponse<RedTeamTenant[]>> {
    return this.get<RedTeamTenant[]>('/red-team/tenants/available');
  }

  async getRedTeamTenants(params?: {
    status?: 'all' | 'available' | 'in_use';
    q?: string;
  }): Promise<ApiResponse<RedTeamTenantListItem[]>> {
    const query = new URLSearchParams();
    if (params?.status) query.set('status', params.status);
    if (params?.q) query.set('q', params.q);
    const qs = query.toString();
    return this.get<RedTeamTenantListItem[]>(
      `/red-team/tenants${qs ? `?${qs}` : ''}`,
    );
  }

  async suggestRedTeamTenant(payload: {
    project_reference: string;
    want_relay?: boolean;
    want_phishing?: boolean;
  }): Promise<ApiResponse<RedTeamTenant & { tenant_provider?: string; project_reference?: string }>> {
    return this.post('/red-team/tenants/suggest', payload);
  }

  async listProjectInfrastructure(reference: string): Promise<ApiResponse<InfrastructureRequest[]>> {
    return this.get<InfrastructureRequest[]>(`/red-team/projects/${encodeURIComponent(reference)}/infrastructure`);
  }

  async listInfrastructure(): Promise<ApiResponse<InfrastructureRequest[]>> {
    return this.get<InfrastructureRequest[]>('/red-team/infrastructure');
  }

  /** @deprecated Use listInfrastructure */
  async listRunningProjectInfrastructure(): Promise<ApiResponse<InfrastructureRequest[]>> {
    return this.listInfrastructure();
  }

  async getInfrastructureRequest(id: number): Promise<ApiResponse<InfrastructureRequest>> {
    return this.get<InfrastructureRequest>(`/red-team/infrastructure/${id}`);
  }

  async createInfrastructureRequest(
    payload: InfrastructureCreatePayload,
  ): Promise<
    ApiResponse<
      InfrastructureRequest & {
        tenant_provider?: string;
        tenant_swapped?: boolean;
      }
    >
  > {
    return this.post('/red-team/infrastructure', payload);
  }

  async requestInfrastructureDestruction(id: number): Promise<ApiResponse<{
    message: string;
    id: number;
    project_reference: string;
    status: string;
    requested_by: string;
    phishing_domains_to_deregister: string[];
  }>> {
    return this.post(`/red-team/infrastructure/${id}/destroy-request`, {});
  }

  async listAdminInfrastructureQueue(): Promise<ApiResponse<InfrastructureRequest[]>> {
    return this.get<InfrastructureRequest[]>('/red-team/admin/infrastructure');
  }

  async completeInfrastructureDeploy(id: number): Promise<ApiResponse<InfrastructureRequest>> {
    return this.post(`/red-team/admin/infrastructure/${id}/complete-deploy`, {});
  }

  async completeInfrastructureTerraformDestroy(id: number): Promise<ApiResponse<InfrastructureRequest>> {
    return this.post(`/red-team/admin/infrastructure/${id}/complete-terraform-destroy`, {});
  }

  async completeInfrastructurePhishingRelease(id: number): Promise<ApiResponse<InfrastructureRequest>> {
    return this.post(`/red-team/admin/infrastructure/${id}/complete-phishing-release`, {});
  }

  async completeInfrastructureBackupDelete(id: number): Promise<ApiResponse<InfrastructureRequest>> {
    return this.post(`/red-team/admin/infrastructure/${id}/complete-backup-delete`, {});
  }

  async cancelInfrastructureRequest(id: number): Promise<ApiResponse<InfrastructureRequest>> {
    return this.post(`/red-team/admin/infrastructure/${id}/cancel`, {});
  }

  async getProjectTenants(reference: string): Promise<ApiResponse<ProjectTenant[]>> {
    return this.get<ProjectTenant[]>(`/red-team/projects/${encodeURIComponent(reference)}/tenants`);
  }

  async ensurePhishingTenant(reference: string): Promise<ApiResponse<{
    assigned: boolean;
    tenant: ProjectTenant;
    message?: string;
  }>> {
    return this.post(`/red-team/projects/${encodeURIComponent(reference)}/phishing/ensure-tenant`, {});
  }

  async getPhishingDomains(reference: string): Promise<ApiResponse<PhishingDomainsResponse>> {
    return this.get<PhishingDomainsResponse>(
      `/red-team/projects/${encodeURIComponent(reference)}/phishing/domains`,
    );
  }

  async submitPhishingDomains(
    reference: string,
    payload: { domains: Array<{ domain: string; subdomains: string[] }>; comment?: string },
  ): Promise<ApiResponse<{ message: string; domains: unknown[] }>> {
    return this.post(
      `/red-team/projects/${encodeURIComponent(reference)}/phishing/submit`,
      payload,
    );
  }

  async deregisterPhishingDomains(
    reference: string,
    domains: string[],
  ): Promise<ApiResponse<{ message: string; domains: unknown[] }>> {
    return this.post(
      `/red-team/projects/${encodeURIComponent(reference)}/phishing/deregister`,
      { domains },
    );
  }

  async refreshPhishingJobs(
    reference: string,
  ): Promise<ApiResponse<PhishingJobsRefreshResponse>> {
    return this.post(
      `/red-team/projects/${encodeURIComponent(reference)}/phishing/jobs/refresh`,
      {},
    );
  }

  async terminatePhishingJob(
    reference: string,
    instanceId: string,
    reason?: string,
  ): Promise<ApiResponse<{ message: string; job: PhishingOngoingJob; status: string }>> {
    return this.post(
      `/red-team/projects/${encodeURIComponent(reference)}/phishing/jobs/${encodeURIComponent(instanceId)}/terminate`,
      { reason: reason || "terminated from backoffice" },
    );
  }

  async restartPhishingJob(
    reference: string,
    instanceId: string,
  ): Promise<ApiResponse<{ message: string; job: PhishingOngoingJob; status: string }>> {
    return this.post(
      `/red-team/projects/${encodeURIComponent(reference)}/phishing/jobs/${encodeURIComponent(instanceId)}/restart`,
      {},
    );
  }
}

// Export singleton instance
export const apiService = new ApiService();
